/**
 * The catalogue of ready analyses (FR-PIL-04, FR-PIL-05, FR-SUI-16): tonnage and cost broken
 * down by a dimension over a period, compared with the previous period of the same length, lead
 * times, stock ageing, compliance counts and year-on-year trends. Pure functions over normalized
 * facts: the API loads the facts, the web draws the chart and the table, the exports print the
 * same rows. Labels are French, the language of the documents; the web translates column keys.
 */
import { z } from 'zod';
import { round3 } from './expedition.js';
import { TREATMENT_FAMILY_LABELS, treatmentFamilyOf } from './indicators.js';
export const ANALYSIS_KEYS = [
    'TONNAGE_BY_WASTE',
    'TONNAGE_BY_ORIGIN',
    'TONNAGE_BY_PRESTATAIRE',
    'TONNAGE_BY_TREATMENT',
    'COST_BY_KIND',
    'COST_BY_WASTE',
    'COST_BY_PRESTATAIRE',
    'COST_BY_ORIGIN',
    'COST_PER_TONNE_OVER_TIME',
    'LEAD_TIMES',
    'STOCK_AGEING',
    'COMPLIANCE',
    'PRESTATAIRE_PERFORMANCE',
    'YEAR_ON_YEAR',
];
export const ANALYSES = [
    {
        key: 'TONNAGE_BY_WASTE',
        category: 'TONNAGE',
        costs: false,
        groupBy: ['waste', 'chapter', 'type', 'month'],
    },
    { key: 'TONNAGE_BY_ORIGIN', category: 'TONNAGE', costs: false, groupBy: ['origin'] },
    {
        key: 'TONNAGE_BY_PRESTATAIRE',
        category: 'TONNAGE',
        costs: false,
        groupBy: ['destination', 'transporter'],
    },
    { key: 'TONNAGE_BY_TREATMENT', category: 'TONNAGE', costs: false, groupBy: ['family'] },
    { key: 'COST_BY_KIND', category: 'COST', costs: true, groupBy: ['kind'] },
    { key: 'COST_BY_WASTE', category: 'COST', costs: true, groupBy: ['waste', 'chapter', 'type'] },
    {
        key: 'COST_BY_PRESTATAIRE',
        category: 'COST',
        costs: true,
        groupBy: ['destination', 'transporter'],
    },
    { key: 'COST_BY_ORIGIN', category: 'COST', costs: true, groupBy: ['origin'] },
    { key: 'COST_PER_TONNE_OVER_TIME', category: 'COST', costs: true, groupBy: ['month'] },
    { key: 'LEAD_TIMES', category: 'DELAYS', costs: false, groupBy: ['month'] },
    { key: 'STOCK_AGEING', category: 'STOCK', costs: false, groupBy: ['waste'], snapshot: true },
    { key: 'COMPLIANCE', category: 'COMPLIANCE', costs: false, groupBy: ['month'] },
    {
        key: 'PRESTATAIRE_PERFORMANCE',
        category: 'COMPLIANCE',
        costs: false,
        groupBy: ['destination'],
    },
    { key: 'YEAR_ON_YEAR', category: 'TREND', costs: false, groupBy: ['month'] },
];
export function analysisDefinition(key) {
    return ANALYSES.find((a) => a.key === key);
}
// ---- helpers ------------------------------------------------------------------------------------
const round2 = (n) => Math.round(n * 100) / 100;
/** Percentages with two decimals: a share of a total, and a change against the period before. */
const sharePct = (part, total) => (total > 0 ? round2((100 * part) / total) : null);
const deltaPct = (now, before) => before === null || before === 0 ? null : round2((100 * (now - before)) / before);
/** The period of the same length ending the day before `from`. */
export function previousPeriod(from, to) {
    const start = new Date(`${from}T00:00:00Z`);
    const end = new Date(`${to}T00:00:00Z`);
    const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
    const prevEnd = new Date(start.getTime() - 86_400_000);
    const prevStart = new Date(prevEnd.getTime() - (days - 1) * 86_400_000);
    return { from: prevStart.toISOString().slice(0, 10), to: prevEnd.toISOString().slice(0, 10) };
}
/** The months of a period as YYYY-MM, in order. */
export function monthsOf(from, to) {
    const out = [];
    let y = Number(from.slice(0, 4));
    let m = Number(from.slice(5, 7));
    const end = to.slice(0, 7);
    for (let i = 0; i < 240; i++) {
        const key = `${y}-${String(m).padStart(2, '0')}`;
        if (key > end)
            break;
        out.push(key);
        m += 1;
        if (m > 12) {
            m = 1;
            y += 1;
        }
    }
    return out;
}
const MONTH_FR = [
    'janv.',
    'févr.',
    'mars',
    'avr.',
    'mai',
    'juin',
    'juil.',
    'août',
    'sept.',
    'oct.',
    'nov.',
    'déc.',
];
export function monthLabel(month) {
    return `${MONTH_FR[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
}
export const COST_KIND_LABELS = {
    TRANSPORT: 'Transport',
    TRAITEMENT: 'Traitement',
    TAXE: 'Taxes',
    ACHAT_CONDITIONNEMENT: 'Achat de conditionnements',
    RECETTE: 'Recettes (ventes)',
    AUTRE_OPERATION: 'Autres opérations',
    LOCATION_CONDITIONNEMENT: 'Location de conditionnements',
    FRAIS_ANNEXES: 'Frais annexes',
};
export const WASTE_TYPE_LABELS = {
    DND: 'Déchets non dangereux',
    DD: 'Déchets dangereux',
    INERTE: 'Déchets inertes',
    DASRI: 'DASRI',
    MATIERES_REVENDUES: 'Matières revendues',
    RADIOACTIF: 'Déchets radioactifs',
    DD_AMIANTE: 'Déchets dangereux amiantés',
    FLUIDES_FRIGORIGENES: 'Fluides frigorigènes',
    TERRES_EXCAVEES: 'Terres excavées',
};
export const UNASSIGNED = 'Non renseigné';
function dimensionOf(groupBy) {
    switch (groupBy) {
        case 'waste':
            return (f) => ({ key: f.wasteId, label: `${f.wasteName} (${f.cmdCode})` });
        case 'chapter':
            return (f) => {
                const ch = f.cmdCode.replace(/\s/g, '').slice(0, 2);
                return { key: ch, label: `Chapitre ${ch}` };
            };
        case 'type':
            return (f) => ({
                key: f.wasteType ?? '—',
                label: WASTE_TYPE_LABELS[f.wasteType ?? ''] ?? UNASSIGNED,
            });
        case 'origin':
            return (f) => ({ key: f.originName ?? '', label: f.originName ?? UNASSIGNED });
        case 'transporter':
            return (f) => ({ key: f.transporterName ?? '', label: f.transporterName ?? UNASSIGNED });
        case 'destination':
            return (f) => ({ key: f.destinationName ?? '', label: f.destinationName ?? UNASSIGNED });
        case 'family':
            return (f) => {
                const fam = treatmentFamilyOf(f.drCode);
                return { key: fam, label: TREATMENT_FAMILY_LABELS[fam].fr };
            };
        case 'site':
            return (f) => ({ key: f.siteId, label: f.siteName });
        case 'month':
            return (f) => ({ key: f.entryDate.slice(0, 7), label: monthLabel(f.entryDate.slice(0, 7)) });
        default:
            return () => null;
    }
}
function costDimensionOf(groupBy) {
    switch (groupBy) {
        case 'kind':
            return (f) => ({ key: f.kind, label: COST_KIND_LABELS[f.kind] ?? f.kind });
        case 'waste':
            return (f) => f.wasteId
                ? { key: f.wasteId, label: `${f.wasteName} (${f.cmdCode})` }
                : { key: '', label: 'Coûts fixes du site' };
        case 'chapter':
            return (f) => {
                if (!f.cmdCode)
                    return { key: '', label: 'Coûts fixes du site' };
                const ch = f.cmdCode.replace(/\s/g, '').slice(0, 2);
                return { key: ch, label: `Chapitre ${ch}` };
            };
        case 'type':
            return (f) => f.wasteType
                ? { key: f.wasteType, label: WASTE_TYPE_LABELS[f.wasteType] ?? f.wasteType }
                : { key: '', label: 'Coûts fixes du site' };
        case 'origin':
            return (f) => ({ key: f.originName ?? '', label: f.originName ?? UNASSIGNED });
        case 'transporter':
            return (f) => f.prestataireRole === 'TRANSPORTER' && f.prestataireName
                ? { key: f.prestataireName, label: f.prestataireName }
                : { key: '', label: 'Hors transporteur' };
        case 'destination':
            return (f) => f.prestataireRole === 'DESTINATION' && f.prestataireName
                ? { key: f.prestataireName, label: f.prestataireName }
                : { key: '', label: 'Hors destinataire' };
        case 'site':
            return (f) => ({ key: f.siteId, label: f.siteName });
        case 'month':
            return (f) => ({ key: f.date.slice(0, 7), label: monthLabel(f.date.slice(0, 7)) });
        default:
            return (f) => ({ key: f.kind, label: COST_KIND_LABELS[f.kind] ?? f.kind });
    }
}
const sumBy = (items, dim, value) => {
    const map = new Map();
    for (const it of items) {
        const d = dim(it);
        if (!d)
            continue;
        const e = map.get(d.key) ?? { dim: d, value: 0 };
        e.value = round3(e.value + value(it));
        map.set(d.key, e);
    }
    return map;
};
/** Tonnage shipped by a dimension, with the share and the change against the previous period. */
export function tonnageAnalysis(input) {
    const dim = dimensionOf(input.groupBy);
    const byMonth = input.groupBy === 'month';
    const now = sumBy(input.lines, dim, (l) => l.quantityT);
    const before = input.previousLines ? sumBy(input.previousLines, dim, (l) => l.quantityT) : null;
    const total = round3([...now.values()].reduce((s, e) => s + e.value, 0));
    const totalBefore = before ? round3([...before.values()].reduce((s, e) => s + e.value, 0)) : null;
    const hazardous = sumBy(input.lines, dim, (l) => (l.hazardous ? l.quantityT : 0));
    const keys = byMonth
        ? monthsOf(input.period.from, input.period.to).map((m) => ({ key: m, label: monthLabel(m) }))
        : [...now.values()]
            .map((e) => e.dim)
            .sort((a, b) => (now.get(b.key)?.value ?? 0) - (now.get(a.key)?.value ?? 0));
    const rows = keys.map((d) => {
        const v = now.get(d.key)?.value ?? 0;
        const b = before ? (before.get(d.key)?.value ?? 0) : null;
        return {
            dimension: d.label,
            shippedT: v,
            hazardousT: hazardous.get(d.key)?.value ?? 0,
            share: sharePct(v, total),
            previousT: b,
            deltaPct: deltaPct(v, b),
        };
    });
    const columns = [
        { key: 'dimension', label: dimensionLabel(input.groupBy) },
        { key: 'shippedT', label: 'Expédié (t)', numeric: true, unit: 't' },
        { key: 'hazardousT', label: 'dont dangereux (t)', numeric: true, unit: 't' },
        { key: 'share', label: 'Part (%)', numeric: true, unit: '%' },
        ...(before
            ? [
                { key: 'previousT', label: 'Période précédente (t)', numeric: true, unit: 't' },
                { key: 'deltaPct', label: 'Évolution (%)', numeric: true, unit: '%' },
            ]
            : []),
    ];
    const result = {
        key: input.key,
        groupBy: input.groupBy,
        period: input.period,
        previous: before ? previousPeriod(input.period.from, input.period.to) : null,
        chart: byMonth ? 'COLUMNS' : 'BARS',
        unit: 't',
        columns,
        rows,
        series: [
            {
                key: 'shippedT',
                label: 'Expédié',
                unit: 't',
                points: rows.map((r) => ({ label: String(r.dimension), value: r.shippedT })),
            },
            ...(before
                ? [
                    {
                        key: 'previousT',
                        label: 'Période précédente',
                        unit: 't',
                        points: rows.map((r) => ({
                            label: String(r.dimension),
                            value: r.previousT,
                        })),
                    },
                ]
                : []),
        ],
        totals: {
            dimension: 'Total',
            shippedT: total,
            hazardousT: round3(input.lines.filter((l) => l.hazardous).reduce((s, l) => s + l.quantityT, 0)),
            share: total > 0 ? 100 : null,
            previousT: totalBefore,
            deltaPct: deltaPct(total, totalBefore),
        },
        notes: [
            'Tonnages du registre : expéditions nettes de leurs corrections, à la date d’enlèvement.',
        ],
    };
    if (input.key === 'TONNAGE_BY_TREATMENT') {
        const recovered = round3(input.lines.filter((l) => /^R/i.test(l.drCode ?? '')).reduce((s, l) => s + l.quantityT, 0));
        const known = round3(input.lines.filter((l) => l.drCode).reduce((s, l) => s + l.quantityT, 0));
        result.totals.valorisationPct = sharePct(recovered, known);
        result.notes.push(`Taux de valorisation : ${result.totals.valorisationPct ?? '—'} % du tonnage à opération connue envoyé vers une opération R.`);
    }
    return result;
}
function dimensionLabel(groupBy) {
    switch (groupBy) {
        case 'waste':
            return 'Déchet';
        case 'chapter':
            return 'Chapitre du catalogue';
        case 'type':
            return 'Type de déchet';
        case 'origin':
            return 'Origine';
        case 'transporter':
            return 'Transporteur';
        case 'destination':
            return 'Destinataire';
        case 'family':
            return 'Famille de traitement';
        case 'kind':
            return 'Nature du coût';
        case 'site':
            return 'Site';
        case 'month':
            return 'Mois';
    }
}
/** Cost by a dimension with its cost per tonne and the change against the previous period. */
export function costAnalysis(input) {
    const dim = costDimensionOf(input.groupBy);
    const tonDim = dimensionOf(input.groupBy);
    const byMonth = input.groupBy === 'month';
    const now = sumBy(input.costs, dim, (c) => c.amount);
    const tons = sumBy(input.lines, tonDim, (l) => l.quantityT);
    const before = input.previousCosts ? sumBy(input.previousCosts, dim, (c) => c.amount) : null;
    const total = round2([...now.values()].reduce((s, e) => s + e.value, 0));
    const totalT = round3(input.lines.reduce((s, l) => s + l.quantityT, 0));
    const totalBefore = before ? round2([...before.values()].reduce((s, e) => s + e.value, 0)) : null;
    const totalTBefore = input.previousLines
        ? round3(input.previousLines.reduce((s, l) => s + l.quantityT, 0))
        : null;
    const keys = byMonth
        ? monthsOf(input.period.from, input.period.to).map((m) => ({ key: m, label: monthLabel(m) }))
        : [...now.values()]
            .map((e) => e.dim)
            .sort((a, b) => (now.get(b.key)?.value ?? 0) - (now.get(a.key)?.value ?? 0));
    const perTonneApplies = [
        'waste',
        'chapter',
        'type',
        'origin',
        'site',
        'month',
        'destination',
        'transporter',
    ].includes(input.groupBy);
    const rows = keys.map((d) => {
        const v = round2(now.get(d.key)?.value ?? 0);
        const t = tons.get(d.key)?.value ?? null;
        const b = before ? round2(before.get(d.key)?.value ?? 0) : null;
        return {
            dimension: d.label,
            amount: v,
            share: sharePct(v, total),
            tonnageT: perTonneApplies ? (t ?? 0) : null,
            perTonne: perTonneApplies && t !== null && t > 0 ? round2(v / t) : null,
            previousAmount: b,
            deltaPct: deltaPct(v, b),
        };
    });
    const columns = [
        { key: 'dimension', label: dimensionLabel(input.groupBy) },
        { key: 'amount', label: 'Coût (MAD)', numeric: true, unit: 'MAD' },
        { key: 'share', label: 'Part (%)', numeric: true, unit: '%' },
        ...(perTonneApplies
            ? [
                { key: 'tonnageT', label: 'Tonnage (t)', numeric: true, unit: 't' },
                { key: 'perTonne', label: 'Coût par tonne (MAD/t)', numeric: true, unit: 'MAD/t' },
            ]
            : []),
        ...(before
            ? [
                { key: 'previousAmount', label: 'Période précédente (MAD)', numeric: true, unit: 'MAD' },
                { key: 'deltaPct', label: 'Évolution (%)', numeric: true, unit: '%' },
            ]
            : []),
    ];
    const series = input.key === 'COST_PER_TONNE_OVER_TIME'
        ? [
            {
                key: 'perTonne',
                label: 'Coût par tonne',
                unit: 'MAD/t',
                points: rows.map((r) => ({
                    label: String(r.dimension),
                    value: r.perTonne,
                })),
            },
        ]
        : [
            {
                key: 'amount',
                label: 'Coût',
                unit: 'MAD',
                points: rows.map((r) => ({ label: String(r.dimension), value: r.amount })),
            },
            ...(before
                ? [
                    {
                        key: 'previousAmount',
                        label: 'Période précédente',
                        unit: 'MAD',
                        points: rows.map((r) => ({
                            label: String(r.dimension),
                            value: r.previousAmount,
                        })),
                    },
                ]
                : []),
        ];
    return {
        key: input.key,
        groupBy: input.groupBy,
        period: input.period,
        previous: before ? previousPeriod(input.period.from, input.period.to) : null,
        chart: byMonth ? 'COLUMNS' : 'BARS',
        unit: input.key === 'COST_PER_TONNE_OVER_TIME' ? 'MAD/t' : 'MAD',
        columns,
        rows,
        series,
        totals: {
            dimension: 'Total',
            amount: total,
            share: total !== 0 ? 100 : null,
            tonnageT: perTonneApplies ? totalT : null,
            perTonne: perTonneApplies && totalT > 0 ? round2(total / totalT) : null,
            previousAmount: totalBefore,
            deltaPct: deltaPct(total, totalBefore),
            previousPerTonne: totalBefore !== null && totalTBefore ? round2(totalBefore / totalTBefore) : null,
        },
        notes: [
            'Coûts des expéditions enlevées dans la période (lignes de coût, recettes en négatif) et coûts fixes des mois couverts (FR-SUI-12).',
            'Coût par tonne : coût rapporté au tonnage expédié de la même ligne ; les coûts fixes n’ont pas de tonnage propre.',
        ],
    };
}
const daysBetween = (a, b) => Math.round((Date.parse(b.slice(0, 10)) - Date.parse(a.slice(0, 10))) / 86_400_000);
const average = (xs) => xs.length ? round2(xs.reduce((s, x) => s + x, 0) / xs.length) : null;
const median = (xs) => {
    if (!xs.length)
        return null;
    const s = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : round2((s[mid - 1] + s[mid]) / 2);
};
/** Days from the pickup request to the pickup, and from the pickup to the return, per month. */
export function leadTimeAnalysis(period, facts) {
    const months = monthsOf(period.from, period.to);
    const rows = months.map((m) => {
        const req = facts.requests
            .filter((r) => r.pickedUpAt?.startsWith(m))
            .map((r) => daysBetween(r.requestedAt, r.pickedUpAt));
        const ret = facts.pickups
            .filter((p) => p.shippedAt.startsWith(m) && p.receivedAt)
            .map((p) => daysBetween(p.shippedAt, p.receivedAt));
        const open = facts.pickups.filter((p) => p.shippedAt.startsWith(m) && !p.receivedAt).length;
        return {
            dimension: monthLabel(m),
            requestToPickup: average(req),
            requestToPickupMedian: median(req),
            pickups: req.length,
            pickupToReturn: average(ret),
            pickupToReturnMedian: median(ret),
            returned: ret.length,
            awaitingReturn: open,
        };
    });
    const allReq = facts.requests
        .filter((r) => r.pickedUpAt)
        .map((r) => daysBetween(r.requestedAt, r.pickedUpAt));
    const allRet = facts.pickups
        .filter((p) => p.receivedAt)
        .map((p) => daysBetween(p.shippedAt, p.receivedAt));
    return {
        key: 'LEAD_TIMES',
        groupBy: 'month',
        period,
        previous: null,
        chart: 'COLUMNS',
        unit: 'days',
        columns: [
            { key: 'dimension', label: 'Mois' },
            {
                key: 'requestToPickup',
                label: 'Demande → enlèvement (jours, moyenne)',
                numeric: true,
                unit: 'days',
            },
            { key: 'requestToPickupMedian', label: 'Médiane', numeric: true, unit: 'days' },
            { key: 'pickups', label: 'Enlèvements', numeric: true, unit: 'count' },
            {
                key: 'pickupToReturn',
                label: 'Enlèvement → retour (jours, moyenne)',
                numeric: true,
                unit: 'days',
            },
            { key: 'pickupToReturnMedian', label: 'Médiane', numeric: true, unit: 'days' },
            { key: 'returned', label: 'Retours', numeric: true, unit: 'count' },
            { key: 'awaitingReturn', label: 'Retours attendus', numeric: true, unit: 'count' },
        ],
        rows,
        series: [
            {
                key: 'requestToPickup',
                label: 'Demande → enlèvement',
                unit: 'days',
                points: rows.map((r) => ({
                    label: String(r.dimension),
                    value: r.requestToPickup,
                })),
            },
            {
                key: 'pickupToReturn',
                label: 'Enlèvement → retour',
                unit: 'days',
                points: rows.map((r) => ({
                    label: String(r.dimension),
                    value: r.pickupToReturn,
                })),
            },
        ],
        totals: {
            dimension: 'Période',
            requestToPickup: average(allReq),
            requestToPickupMedian: median(allReq),
            pickups: allReq.length,
            pickupToReturn: average(allRet),
            pickupToReturnMedian: median(allRet),
            returned: allRet.length,
            awaitingReturn: facts.pickups.filter((p) => !p.receivedAt).length,
        },
        notes: [
            'Demande → enlèvement : de la demande d’enlèvement à la date d’enlèvement réelle. Enlèvement → retour : de l’enlèvement à l’enregistrement de la réception par le destinataire.',
        ],
    };
}
const AGE_BUCKETS = [
    { key: 'b0', label: '0 à 30 jours', min: 0, max: 30 },
    { key: 'b1', label: '31 à 90 jours', min: 31, max: 90 },
    { key: 'b2', label: '91 à 180 jours', min: 91, max: 180 },
    { key: 'b3', label: 'Plus de 180 jours', min: 181, max: null },
];
/** The stock on site today by age of its oldest tonnage, and the wastes beyond their limit. */
export function stockAgeingAnalysis(today, balances) {
    const live = balances.filter((b) => b.balanceT > 0);
    const bucketOf = (age) => AGE_BUCKETS.find((b) => age !== null && age >= b.min && (b.max === null || age <= b.max)) ??
        AGE_BUCKETS[0];
    const rows = live
        .sort((a, b) => (b.ageDays ?? 0) - (a.ageDays ?? 0))
        .map((b) => ({
        dimension: `${b.wasteName} (${b.cmdCode})`,
        balanceT: b.balanceT,
        hazardous: b.hazardous ? 'Oui' : 'Non',
        ageDays: b.ageDays,
        bucket: bucketOf(b.ageDays).label,
        maxStorageDays: b.maxStorageDays,
        overdue: b.maxStorageDays !== null && b.ageDays !== null && b.ageDays > b.maxStorageDays
            ? 'Dépassé'
            : '',
    }));
    const series = [
        {
            key: 'balanceT',
            label: 'Stock',
            unit: 't',
            points: AGE_BUCKETS.map((bk) => ({
                label: bk.label,
                value: round3(live
                    .filter((b) => bucketOf(b.ageDays).key === bk.key)
                    .reduce((s, b) => s + b.balanceT, 0)),
            })),
        },
    ];
    return {
        key: 'STOCK_AGEING',
        groupBy: 'waste',
        period: { from: today, to: today },
        previous: null,
        chart: 'BARS',
        unit: 't',
        columns: [
            { key: 'dimension', label: 'Déchet' },
            { key: 'balanceT', label: 'Stock (t)', numeric: true, unit: 't' },
            { key: 'hazardous', label: 'Dangereux' },
            { key: 'ageDays', label: 'Âge du plus ancien (jours)', numeric: true, unit: 'days' },
            { key: 'bucket', label: 'Tranche' },
            { key: 'maxStorageDays', label: 'Durée maximale (jours)', numeric: true, unit: 'days' },
            { key: 'overdue', label: 'Dépassement' },
        ],
        rows,
        series,
        totals: {
            dimension: 'Total',
            balanceT: round3(live.reduce((s, b) => s + b.balanceT, 0)),
            hazardous: null,
            ageDays: live.reduce((m, b) => (b.ageDays !== null && (m === null || b.ageDays > m) ? b.ageDays : m), null),
            bucket: null,
            maxStorageDays: null,
            overdue: String(rows.filter((r) => r.overdue === 'Dépassé').length),
        },
        notes: [
            'Stock sur site à ce jour ; l’âge est celui du tonnage le plus ancien encore présent (FR-COL-10).',
        ],
    };
}
/** Exemptions recorded, estimated quantities and expired documents, per month. */
export function complianceAnalysis(period, facts) {
    const months = monthsOf(period.from, period.to);
    const rows = months.map((m) => {
        const c = facts.chargements.filter((x) => x.actualDate.startsWith(m));
        const e = facts.expeditions.filter((x) => x.shippedAt.startsWith(m));
        const estimated = e.filter((x) => x.estimated).length;
        return {
            dimension: monthLabel(m),
            chargements: c.length,
            checklistExemptions: c.reduce((s, x) => s + x.exemptions, 0),
            ruleExemptions: e.reduce((s, x) => s + x.exemptions, 0),
            expeditions: e.length,
            estimated,
            estimatedPct: sharePct(estimated, e.length),
        };
    });
    const allE = facts.expeditions;
    return {
        key: 'COMPLIANCE',
        groupBy: 'month',
        period,
        previous: null,
        chart: 'COLUMNS',
        unit: 'count',
        columns: [
            { key: 'dimension', label: 'Mois' },
            { key: 'chargements', label: 'Chargements', numeric: true, unit: 'count' },
            {
                key: 'checklistExemptions',
                label: 'Dérogations au contrôle de départ',
                numeric: true,
                unit: 'count',
            },
            {
                key: 'ruleExemptions',
                label: 'Dérogations aux règles métier',
                numeric: true,
                unit: 'count',
            },
            { key: 'expeditions', label: 'Expéditions', numeric: true, unit: 'count' },
            { key: 'estimated', label: 'Quantités estimées', numeric: true, unit: 'count' },
            { key: 'estimatedPct', label: 'Quantités estimées (%)', numeric: true, unit: '%' },
        ],
        rows,
        series: [
            {
                key: 'checklistExemptions',
                label: 'Dérogations au départ',
                unit: 'count',
                points: rows.map((r) => ({
                    label: String(r.dimension),
                    value: r.checklistExemptions,
                })),
            },
            {
                key: 'ruleExemptions',
                label: 'Dérogations aux règles',
                unit: 'count',
                points: rows.map((r) => ({
                    label: String(r.dimension),
                    value: r.ruleExemptions,
                })),
            },
            {
                key: 'estimated',
                label: 'Quantités estimées',
                unit: 'count',
                points: rows.map((r) => ({ label: String(r.dimension), value: r.estimated })),
            },
        ],
        totals: {
            dimension: 'Période',
            chargements: facts.chargements.length,
            checklistExemptions: facts.chargements.reduce((s, x) => s + x.exemptions, 0),
            ruleExemptions: allE.reduce((s, x) => s + x.exemptions, 0),
            expeditions: allE.length,
            estimated: allE.filter((x) => x.estimated).length,
            estimatedPct: sharePct(allE.filter((x) => x.estimated).length, allE.length),
            expiredAuthorizations: facts.expiredAuthorizations,
            expiredDocuments: facts.expiredDocuments,
        },
        notes: [
            `À ce jour : ${facts.expiredAuthorizations} autorisation(s) et ${facts.expiredDocuments} document(s) échus chez les prestataires activés.`,
        ],
    };
}
// ---- year on year -------------------------------------------------------------------------------
/** The tonnage shipped each month of a year next to the same month of the year before. */
export function yearOnYearAnalysis(year, lines, previousLines) {
    const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
    const sum = (ls, y, m) => round3(ls.filter((l) => l.entryDate.startsWith(`${y}-${m}`)).reduce((s, l) => s + l.quantityT, 0));
    const rows = months.map((m) => {
        const now = sum(lines, year, m);
        const before = sum(previousLines, year - 1, m);
        return {
            dimension: MONTH_FR[Number(m) - 1],
            currentT: now,
            previousT: before,
            deltaPct: deltaPct(now, before),
        };
    });
    const total = round3(lines.reduce((s, l) => s + l.quantityT, 0));
    const totalBefore = round3(previousLines.reduce((s, l) => s + l.quantityT, 0));
    return {
        key: 'YEAR_ON_YEAR',
        groupBy: 'month',
        period: { from: `${year}-01-01`, to: `${year}-12-31` },
        previous: { from: `${year - 1}-01-01`, to: `${year - 1}-12-31` },
        chart: 'COLUMNS',
        unit: 't',
        columns: [
            { key: 'dimension', label: 'Mois' },
            { key: 'currentT', label: `${year} (t)`, numeric: true, unit: 't' },
            { key: 'previousT', label: `${year - 1} (t)`, numeric: true, unit: 't' },
            { key: 'deltaPct', label: 'Évolution (%)', numeric: true, unit: '%' },
        ],
        rows,
        series: [
            {
                key: 'currentT',
                label: String(year),
                unit: 't',
                points: rows.map((r) => ({ label: String(r.dimension), value: r.currentT })),
            },
            {
                key: 'previousT',
                label: String(year - 1),
                unit: 't',
                points: rows.map((r) => ({ label: String(r.dimension), value: r.previousT })),
            },
        ],
        totals: {
            dimension: 'Total',
            currentT: total,
            previousT: totalBefore,
            deltaPct: deltaPct(total, totalBefore),
        },
        notes: [
            'Tonnages expédiés du registre, mois par mois, contre le même mois de l’année précédente.',
        ],
    };
}
/** The family of a shipped line's planned operation, for the API's own breakdowns. */
export const familyOf = (drCode) => treatmentFamilyOf(drCode);
/** A monthly e-mail of an analysis (FR-PIL-05): the month before, as a spreadsheet. */
export const analysisSubscriptionSchema = z
    .object({
    /** An analysis of the catalogue, or a saved analysis (FR-PIL-08). */
    analysisKey: z.enum(ANALYSIS_KEYS).optional(),
    savedAnalysisId: z.string().uuid().optional(),
    groupBy: z
        .enum([
        'waste',
        'chapter',
        'type',
        'origin',
        'transporter',
        'destination',
        'family',
        'kind',
        'site',
        'month',
    ])
        .optional(),
    recipients: z.array(z.string().trim().email().max(200)).min(1).max(10),
})
    .refine((v) => Boolean(v.analysisKey) !== Boolean(v.savedAnalysisId), {
    message: 'Give an analysis of the catalogue or a saved analysis',
});
/** The month before a date, as its first and last day. */
export function previousMonth(today) {
    const y = Number(today.slice(0, 4));
    const m = Number(today.slice(5, 7));
    const py = m === 1 ? y - 1 : y;
    const pm = m === 1 ? 12 : m - 1;
    const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
    const month = `${py}-${String(pm).padStart(2, '0')}`;
    return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}`, month };
}
//# sourceMappingURL=analyses.js.map